import { useQuery } from '@apollo/client';
import { Add } from '@mui/icons-material';
import LoadingButton from '@mui/lab/LoadingButton';
import { Button, MenuItem, Select, Typography, type DialogProps } from '@mui/material';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useAddChannelsToChannelGuide } from '~/api/channel-guides';
import { Dialog, DialogContentTitle, DialogTitle } from '~/components/dialogs/components';
import { DialogActions, DialogContent } from '~/components/dialogs/lib/styles';
import { useAppContext } from '~/contexts';
import { assert } from '~/lib/assert';
import { AvailableChannelGuidesDocument } from '../queries/available-channel-guides.generated';

export interface AddChannelToGuideDialogProps extends Omit<DialogProps, 'children'> {
  channelId: number;
  close: () => void;
}

export const AddChannelToGuideDialog = ({
  channelId,
  close,
  ...props
}: AddChannelToGuideDialogProps) => {
  const { t } = useTranslation();
  const { currentNetwork } = useAppContext();

  const { data } = useQuery(AvailableChannelGuidesDocument, {
    variables: { networkId: currentNetwork.id },
  });

  const [addChannelsToChannelGuide, { called, reset, loading }] = useAddChannelsToChannelGuide();

  const [channelGuideId, setChannelGuideId] = useState<number | ''>('');

  const channelGuides = data?.network?.allChannelGuides ?? [];

  return (
    <Dialog
      fullWidth
      maxWidth="xs"
      onClose={close}
      TransitionProps={{ onExited: () => setChannelGuideId('') }}
      {...props}
    >
      <DialogTitle onClose={close}>{t('pages.channels.dialogs.add_to_guide.title')}</DialogTitle>

      <DialogContent>
        <DialogContentTitle>
          {t('pages.channels.dialogs.add_to_guide.description')}
        </DialogContentTitle>
        <label>
          <Typography gutterBottom>
            {t('pages.channels.dialogs.add_to_guide.channel_guide_label')}
          </Typography>
          <Select
            displayEmpty
            fullWidth
            onChange={(event) =>
              typeof event.target.value === 'number' && setChannelGuideId(event.target.value)
            }
            value={channelGuideId}
          >
            <MenuItem disabled value="">
              {t('pages.channels.dialogs.add_to_guide.select_placeholder')}
            </MenuItem>
            {channelGuides.map((channelGuide) => (
              <MenuItem key={channelGuide.id} value={channelGuide.id}>
                {channelGuide.name}
              </MenuItem>
            ))}
          </Select>
        </label>
      </DialogContent>

      <DialogActions>
        <Button disabled={called} onClick={close} variant="outlined">
          {t('common.dialogs.cancel')}
        </Button>
        <LoadingButton
          loading={loading}
          loadingPosition="start"
          color="primary"
          disabled={channelGuideId === ''}
          onClick={async () => {
            assert(channelGuideId !== '', 'no channel guide selected');
            assert(data != null, 'no channel id selected');
            await addChannelsToChannelGuide({
              variables: { input: { channelGuideId, channelIds: [channelId] } },
            });
            close();
            reset();
          }}
          variant="contained"
          startIcon={<Add />}
        >
          {t('common.dialogs.add')}
        </LoadingButton>
      </DialogActions>
    </Dialog>
  );
};
